import { useState, useEffect, useRef } from 'react'
import { isFilterOperation, isGroupByOperation, isSortByOperation, type BoundOperation } from '../../OperationTypes.js'
import type { VisualizationPipeline } from '../../RunPipeline.js'
import { type MetadataFields, type Metadatafieldtypes } from '../../MetadataFields.js'
import type { GroupByOperationKey, ProjectionOperationKey, FilterOperationKey, SortByOperationKey } from '../../OperationRegistry.js'
import { OPERATIONS } from '../../OperationRegistry.js'
import { FilterEditorRow } from './FilterEditorRow.js'
import { GroupByEditorRow } from './GroupByEditorRow.js'
import { SortByEditorRow } from './SortByEditorRow.tsx'
import type { VisualizationSampleContainer } from '../../VisualizationSampleContainer.js'

type GroupByWithOptionalProjection = {
  groupBy: BoundOperation<GroupByOperationKey, unknown>
  projection?: BoundOperation<ProjectionOperationKey, unknown>
}

export function BasicModeEditor({
  pipeline,
  setPipeline,
  samples,
  metadataFields
}: {
  pipeline: VisualizationPipeline
  setPipeline: (p: VisualizationPipeline) => void
  samples: VisualizationSampleContainer[]
  metadataFields: MetadataFields
}) {
  const [filters, setFilters] = useState<BoundOperation<FilterOperationKey, unknown>[]>(pipeline.filters ?? [])
  const [groupings, setGroupings] = useState<GroupByWithOptionalProjection[]>([])
  const [sortBys, setSortBys] = useState<BoundOperation<SortByOperationKey, unknown>[]>([])
  const initializedRef = useRef(false)
  const [showFilters, setShowFilters] = useState(false)
  const [showSorts, setShowSorts] = useState(false)

  useEffect(() => {
    if (!initializedRef.current) {
      setFilters(pipeline.filters ?? [])
      const projectionRecord: Record<string, BoundOperation<ProjectionOperationKey, unknown>> = {}
      pipeline.projections.forEach((x) => (projectionRecord[x.metaDataField] = x))
      setGroupings(
        (pipeline.groupBys ?? []).map((x) => ({
          groupBy: x,
          projection: projectionRecord[x.metaDataField]
        }))
      )
      initializedRef.current = true
    }
  }, [pipeline])

  // Update parent pipeline whenever local filters change
  useEffect(() => {
    if (!initializedRef.current) return

    const groupBys = groupings.map((g) => g.groupBy)
    const projections = groupings.flatMap((g) => (g.projection ? [g.projection] : []))

    setPipeline({
      ...pipeline,
      filters,
      groupBys,
      projections,
      sortBys
    })
  }, [filters, groupings, sortBys])

  const handleAddFilter = () => {
    const [firstFieldKey] = Object.keys(metadataFields)
    const firstField = metadataFields[firstFieldKey]
    const firstOp = OPERATIONS.filter((op) => isFilterOperation(op)).find((op) => op.applicableTo.includes(firstField.type))

    if (!firstOp) return

    setFilters([
      ...filters,
      {
        operationKey: firstOp.key,
        metaDataField: firstFieldKey as Metadatafieldtypes,
        config: firstOp.config?.defaultConfig ?? {}
      }
    ])
  }

  const updateFilter = (index: number, updated: BoundOperation<FilterOperationKey, unknown>) => {
    const next = [...filters]
    next[index] = updated
    setFilters(next)
  }

  const removeFilter = (index: number) => {
    const next = [...filters]
    next.splice(index, 1)
    setFilters(next)
  }

  const handleAddGrouping = () => {
    const [firstFieldKey] = Object.keys(metadataFields)
    const firstField = metadataFields[firstFieldKey]
    const groupOp = OPERATIONS.filter((op) => isGroupByOperation(op)).find((op) => op.applicableTo.includes(firstField.type))
    if (!groupOp) return

    setGroupings([
      ...groupings,
      {
        groupBy: {
          operationKey: groupOp.key,
          metaDataField: firstFieldKey as Metadatafieldtypes,
          config: groupOp.config?.defaultConfig ?? {}
        }
      }
    ])
  }

  const updateGrouping = (index: number, updated: GroupByWithOptionalProjection) => {
    const next = [...groupings]
    next[index] = updated
    setGroupings(next)
    setSortBys(sortBys.filter((s) => next.find((n) => n.groupBy.metaDataField === s.metaDataField)))
  }

  const removeGrouping = (index: number) => {
    const next = [...groupings]
    next.splice(index, 1)
    setGroupings(next)
  }

  const updateSorting = (index: number, updatedSortOperation: BoundOperation<SortByOperationKey, unknown>) => {
    setSortBys([...sortBys.slice(0, index), updatedSortOperation, ...sortBys.slice(index + 1)])
  }

  const removeSorting = (index: number) => {
    setSortBys([...sortBys.slice(0, index), ...sortBys.slice(index + 1)])
  }

  const handleAddSorting = () => {
    const firstFieldKey = groupings[0]?.groupBy.metaDataField //Object.keys(metadataFields)
    if (!firstFieldKey) return
    const firstField = metadataFields[firstFieldKey]
    const sortingOp = OPERATIONS.filter((op) => isSortByOperation(op)).find((op) => op.applicableTo.includes(firstField.type))
    if (!sortingOp) return

    setSortBys([
      ...sortBys,
      {
        operationKey: sortingOp.key,
        metaDataField: firstFieldKey as Metadatafieldtypes,
        config: sortingOp.config?.defaultConfig ?? {}
      }
    ])
  }

  return (
    <div className="flex flex-col gap-4">
      {/* FILTER SECTION */}
      {(filters.length > 0 || showFilters) && (
        <div className="border rounded-sm p-3 bg-base-100">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium">Filters</span>
            <button className="btn btn-sm btn-outline btn-success" onClick={handleAddFilter}>
              + Add Filter
            </button>
          </div>

          {filters.length === 0 && <p className="text-sm text-gray-400">No filters added.</p>}

          <div className="flex flex-col gap-2">
            {filters.map((filter, index) => (
              <FilterEditorRow
                metadataFields={metadataFields}
                key={index}
                boundFilterOperation={filter}
                onChange={(f) => updateFilter(index, f)}
                onRemove={() => removeFilter(index)}
                samples={samples}
              />
            ))}
          </div>
        </div>
      )}

      {/* GROUP BY SECTION */}
      <div className="border shadow-sm rounded-sm p-3 bg-base-100">
        <div className="flex justify-between items-center mb-2">
          <span className="font-medium">Group By</span>
          <button className="btn btn-sm btn-outline btn-success" onClick={handleAddGrouping}>
            + Add Grouping
          </button>
        </div>

        {groupings.length === 0 && <p className="text-sm text-gray-400">No grouping added.</p>}

        <div className="flex flex-col gap-2">
          {groupings.map((g, i) => (
            <GroupByEditorRow
              metadataFields={metadataFields}
              key={i}
              value={g}
              onChange={(newVal) => updateGrouping(i, newVal)}
              onRemove={() => removeGrouping(i)}
              samples={samples}
            />
          ))}
        </div>
      </div>

      {/* SORT BY SECTION */}
      {(showSorts || sortBys.length > 0) && (
        <div className="border rounded-sm p-3 bg-base-100">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium">Sort By</span>
            {sortBys.length == 0 && (
              <button className="btn btn-sm btn-outline btn-success" onClick={handleAddSorting}>
                + Add Sorting
              </button>
            )}
          </div>
          {sortBys.length === 0 && <p className="text-sm text-gray-400">No sorting added.</p>}

          <div className="flex flex-col gap-2">
            {sortBys.map((sort, i) => (
              <SortByEditorRow
                metadataFields={groupings.reduce((acc, cur) => {
                  acc[cur.groupBy.metaDataField] = metadataFields[cur.groupBy.metaDataField]
                  return acc
                }, {} as MetadataFields)}
                key={i}
                boundSortByOperation={sort}
                onChange={(newVal) => updateSorting(i, newVal)}
                onRemove={() => removeSorting(i)}
                samples={samples}
              />
            ))}
          </div>
        </div>
      )}
      {sortBys.length <= 0 && filters.length <= 0 && (
        <div className="flex gap-2 justify-between items-center">
          <button
            className="flex-grow-1 btn btn-sm btn-outline btn-success"
            disabled={filters.length > 0}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button
            className="flex-grow-1 btn btn-sm btn-outline btn-success"
            disabled={sortBys.length > 0}
            onClick={() => setShowSorts(!showSorts)}
          >
            {showSorts ? 'Hide Sorting' : 'Show Sorting'}
          </button>
        </div>
      )}
    </div>
  )
}
